import { z } from 'zod'

/**
 * What a solution is charged by.
 *
 * Mirrors `SolutionPricingBasis` in prisma/schema.prisma. A solution may carry
 * more than one — cable bracing is commonly labor by the hour PLUS cable by
 * the foot, and a flat fee per tree PLUS cable by the foot is the same idea —
 * but at most one of each.
 */
export const SOLUTION_PRICING_BASES = [
  'TIME',
  'LINEAR_FEET',
  'DBH_INCH',
  'PER_TREE',
] as const

export const solutionPricingBasisSchema = z.enum(SOLUTION_PRICING_BASES)

export type SolutionPricingBasis = z.infer<typeof solutionPricingBasisSchema>

/**
 * A basis the surveyor types a number for, on the chosen treatment.
 *
 * Trunk diameter is measured once on the tree, and a per-tree fee is a count
 * of one. Anything else is not a typed quantity until it is named here and
 * given a field to land in — a new basis must not be stored as feet.
 */
export type QuantityBasis = Extract<SolutionPricingBasis, 'TIME' | 'LINEAR_FEET'>

export const SOLUTION_PRICING_BASIS_LABELS: Record<
  SolutionPricingBasis,
  string
> = {
  TIME: 'Time',
  LINEAR_FEET: 'Linear feet',
  DBH_INCH: 'Trunk diameter (DBH)',
  PER_TREE: 'Per tree',
}

/** The unit a rate is per, for a settings form: "$__ per hour". */
export const SOLUTION_PRICING_RATE_UNITS: Record<
  SolutionPricingBasis,
  string
> = {
  TIME: 'per hour',
  LINEAR_FEET: 'per foot',
  DBH_INCH: 'per inch',
  PER_TREE: 'per tree',
}

/** The unit a QUANTITY is in, which is not the same as the rate's unit. */
export const SOLUTION_PRICING_QUANTITY_UNITS: Record<
  SolutionPricingBasis,
  string
> = {
  // Minutes, priced against an hourly rate: a surveyor thinks in minutes and
  // nobody should be doing the division in their head on a truck hood.
  TIME: 'minutes',
  LINEAR_FEET: 'feet',
  DBH_INCH: 'inches',
  // Never asked for — see `basisNeedsQuantity` — so this only completes the map.
  PER_TREE: 'tree',
}

/**
 * Whether the surveyor supplies this quantity per tree.
 *
 * DBH does not: it is measured once on the TREE, so a solution priced by it
 * reads the tree's diameter rather than asking again. Three DBH-priced
 * solutions asking separately would be three chances for one tree to disagree
 * with itself about its own trunk.
 *
 * Per tree does not either, for the opposite reason: its quantity is one tree,
 * which nobody has anything to measure or count.
 */
export function basisNeedsQuantity(
  basis: SolutionPricingBasis,
): basis is QuantityBasis {
  switch (basis) {
    case 'TIME':
    case 'LINEAR_FEET': {
      // Assigning into `QuantityBasis` is what keeps the predicate honest.
      // Listing a new basis here without naming it above fails to compile,
      // and the survey then has to say which field the number is.
      const checked: QuantityBasis = basis
      return checked === basis
    }
    case 'DBH_INCH':
    case 'PER_TREE':
      return false
    default: {
      const unhandled: never = basis
      return unhandled
    }
  }
}
